/**
 * 盤面の分割(#152)。
 *
 * 正本は [ADR-0029](../../../docs/adr/0029-panel-split-grid.md)。
 *
 * ## 記法
 *
 * ```
 * 16:9        16+16:9        4+16+4:9        16:9+9+9        16:9+9
 * ```
 *
 * | 規則 | |
 * |---|---|
 * | 1 | まず `:` で横と縦を分ける |
 * | 2 | **各側の `+` が、その軸の分割** |
 * | 3 | 全体の比率 = 横の項の和 : 縦の項の和 |
 * | 4 | **セルの数 = 横の項数 × 縦の項数**(両軸にあれば格子) |
 *
 * **`24:9` と `4+16+4:9` は別物。** 比は同じでも分割が違う ——
 * だから**データは記法ではなく項の配列で持つ**(ADR-0029 の決定 1)。
 * 記法は UI の入出力形式にとどめ、将来 GUI で分割を編集するときに
 * 文字列を組み立て直さずに済むようにする。
 *
 * ## なぜ `packages/shared` に置くか
 *
 * **UI(S04 の入力欄)と検証(API の zod)の両方が使う。** 片方に置くと
 * もう片方が同じ規則を書き直すことになり、**受け付ける式がずれる。**
 *
 * **すべて純粋関数。** DOM も WebGL も触らない。
 */
import { isFolded } from './folds.js';
/** 片方の軸に置ける項の数の上限。 */
export const GRID_MAX_TERMS = 16;
/**
 * 項として受け付ける形。**整数 7 桁 + 小数 6 桁まで。**
 *
 * この範囲に収めると `String(number)` が**必ず指数表記にならない**ので、
 * [`formatGrid`](#formatGrid) → [`parseGrid`](#parseGrid) が必ず元に戻る。
 *
 * 符号・指数表記・`0x` を許さない。`Number()` に丸投げすると
 * `Infinity` や `0x10` まで通ってしまう。
 */
const TERM_PATTERN = /^\d{1,7}(?:\.\d{1,6})?$/;
/**
 * 分割の項として妥当な数値か。**API の検証(zod)もこれを使う** ——
 * 受け付ける範囲を 1 か所で定義するため。
 */
export function isGridTerm(value) {
    if (!Number.isFinite(value) || value <= 0 || value >= 1e7)
        return false;
    /*
     * **小数第 6 位まで。** [`TERM_PATTERN`](#TERM_PATTERN) と範囲をそろえる ——
     * ここだけ緩いと、**API が受け付けた値を入力欄が読めなくなる**
     * (`16.1234567` は保存できるのに「数として読めません」と出続ける)。
     */
    return Math.round(value * 1e6) / 1e6 === value;
}
/** 全角の数字・記号を半角に直す。日本語 IME からの入力を素通しさせる。 */
function toHalfWidth(text) {
    return text
        .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
        .replace(/[：︓]/g, ':')
        .replace(/[＋]/g, '+')
        .replace(/[．]/g, '.');
}
/** 片方の軸を解析する。失敗したら理由(日本語)を返す。 */
function parseAxis(text, side) {
    const parts = text.split('+');
    if (parts.length > GRID_MAX_TERMS) {
        return { reason: `${side}の分割は ${String(GRID_MAX_TERMS)} 個までです` };
    }
    // 軸そのものが空(`16:` / `:9`)。**打鍵の途中で最も出る形**なので、
    // 「+ の前後に」ではなく「入れてください」と言う。
    if (parts.length === 1 && text.trim() === '') {
        return { reason: `${side}を入れてください(例: 16:9)` };
    }
    const terms = [];
    for (const part of parts) {
        const term = part.trim();
        if (term === '') {
            return { reason: `${side}の「+」の前後に数を入れてください(例: 16+16:9)` };
        }
        if (!TERM_PATTERN.test(term)) {
            return { reason: `${side}の「${term}」は数として読めません(例: 16 / 16.5)` };
        }
        const value = Number(term);
        if (!isGridTerm(value)) {
            return { reason: `${side}の「${term}」は 0 より大きい数にしてください` };
        }
        terms.push(value);
    }
    return { terms };
}
/**
 * 記法を解析する。`"4+16+4:9"` → `{ columns: [4, 16, 4], rows: [9] }`。
 *
 * **弾くもの**: 空 / `:` が無い / `:` が 2 つ以上 / 項が空(`16++4`・`+16`・`16+`)/
 * 数でない / `0` 以下 / 符号や指数表記 / 項が多すぎる。
 *
 * 空白は無視する(`16 + 16 : 9` は通る)。全角の `１６：９` も通る。
 */
export function parseGrid(text) {
    const normalized = toHalfWidth(text).trim();
    if (normalized === '') {
        return { ok: false, reason: '比率を入力してください(例: 16:9)' };
    }
    const sides = normalized.split(':');
    if (sides.length === 1) {
        return { ok: false, reason: '横と縦を「:」で区切ってください(例: 16:9)' };
    }
    if (sides.length > 2) {
        return { ok: false, reason: '「:」は 1 つだけです(例: 16:9+9)' };
    }
    const [columnText = '', rowText = ''] = sides;
    const columns = parseAxis(columnText, '横');
    if ('reason' in columns)
        return { ok: false, reason: columns.reason };
    const rows = parseAxis(rowText, '縦');
    if ('reason' in rows)
        return { ok: false, reason: rows.reason };
    return { ok: true, grid: { columns: columns.terms, rows: rows.terms } };
}
/**
 * 記法に組み立てる。[`parseGrid`](#parseGrid) の逆。
 *
 * **`parseGrid` が受け付けた `Grid` なら必ず元の値に戻る。**
 * 項の範囲は [`isGridTerm`](#isGridTerm) が保証しており、その範囲では
 * `String(number)` が指数表記にならない。
 */
export function formatGrid(grid) {
    const side = (terms) => terms.map((t) => String(t)).join('+');
    return `${side(grid.columns)}:${side(grid.rows)}`;
}
/**
 * 各軸の**境界の位置**(累積和)。項が n 個なら n + 1 個返る。
 *
 * **隣り合うセルはこの配列の同じ要素を共有する。** 幅を足し合わせて境界を出すと
 * 浮動小数の誤差で**わずかにずれ、境目に隙間が出る** —— 累積和を 1 度だけ作り、
 * 両側がそれを参照するのが要点(ADR-0029 の「境目は頂点を共有させる」)。
 */
function edgesOf(terms) {
    const edges = [0];
    let sum = 0;
    for (const term of terms) {
        sum += term;
        edges.push(sum);
    }
    return edges;
}
/**
 * 盤面全体の比率。項の和。
 *
 * `4+16+4:9` なら `{ width: 24, height: 9 }`。**`24:9` と同じ値になる** ——
 * 全体の形は同じで、違うのは分割だけだから。
 */
export function gridTotal(grid) {
    const columns = edgesOf(grid.columns);
    const rows = edgesOf(grid.rows);
    return {
        width: columns[columns.length - 1] ?? 0,
        height: rows[rows.length - 1] ?? 0,
    };
}
/**
 * `aspect` を `grid` に移す(#152 / ADR-0029 の「移行・互換性」)。
 *
 * `aspect: { width: 16, height: 9 }` は `16:9`、つまり**分割しない盤面**と
 * まったく同じ意味である。両方を持たせると**食い違ったときにどちらが正か
 * 決められない**ので、読み込みの時点で `grid` に寄せる。
 *
 * **`aspect` だけを持つ既存の `project.json` を壊さない**ための移行であり、
 * 新しく書き出すのは `grid` のみ。
 */
export function gridFromAspect(aspect) {
    return { columns: [aspect.width], rows: [aspect.height] };
}
/** セルの数。**行優先の通し番号はこの数未満。** */
export function cellCount(grid) {
    return grid.columns.length * grid.rows.length;
}
/**
 * **分割を指定していない枠も数えられるセル数**(#241)。
 *
 * **未指定は 1。** 描画コアは分割が無ければ枠全体を 1 セルとして扱う
 * ([ADR-0029](../../../docs/adr/0029-panel-split-grid.md))—— 連動先を数えるときも同じ扱いにする。
 */
export function cellCountOf(grid) {
    return grid == null ? 1 : cellCount(grid);
}
/**
 * **素材の入れ物の数。** 平面の枠はセルの数、**折れた枠は 1 つ**
 * ([ADR-0057](../../../docs/adr/0057-folded-frame.md) の決定 4 —— 折れた枠は素材が全体で 1 本)。
 *
 * **入れ物を数えるところはこれを使う** —— 空き探し(ドロップ・`assign`)、埋め込み先に知らせるセルの数
 * (`signa:ready` の `cellCount` と `assignedCount` の分母)。**外部キーのセル番号の範囲は分割の数のまま**
 * ([`cellCountOf`](#cellCountOf))—— 折れた枠でもセル 1 以降のキーは残り、どれで入れてもセル 0 に寄る(#339)。
 */
export function slotCountOf(frame) {
    return isFolded(frame) ? 1 : cellCountOf(frame.grid);
}
/**
 * **連動先のセル**(#241 / [ADR-0039](../../../docs/adr/0039-frame-link-assignment.md))。**自分を含む。**
 *
 * ## 何を返すか
 *
 * `target` の枠に**連動番号が付いていれば**、同じ番号の枠のうち
 * **同じセル番号を持つもの**を返す。付いていなければ **`target` だけ**。
 *
 * ```
 * 枠A(2分割・番号1) 枠B(2分割・番号1) 枠C(1セル・番号1) 枠D(番号なし)
 *   ↑ セル0に落とす  →  A#0 / B#0 / C#0 が返る(D は別、C#1 は無い)
 * ```
 *
 * ## なぜセル番号で対応させるか
 *
 * **全セルに同じ素材を配ると、分割の意味が消える。** 分割は
 * 「1 枚の盤面に複数の素材を入れる」ため([ADR-0029](../../../docs/adr/0029-panel-split-grid.md))で、
 * **「左の面と右の面に別々の素材、それを 3 セット並べる」が分割 + 連動の自然な使い方**になる。
 *
 * ## なぜ自分を含めるか
 *
 * **呼び出し側が連動の有無で分岐しないため。** 常に「返ってきたセルすべてに入れる」と書けば、
 * 番号が無い枠でも同じコードが通る —— **分岐が増えていたら設計が漏れている。**
 *
 * ## 対応先が無いセル
 *
 * **セル数が足りない枠は飛ばす。** 2 分割の枠と 1 セルの枠を同じ番号にできる(同意書 Q2)ので、
 * セル 1 に落としたときは 1 セルの枠に**何も入らない。**
 *
 * ## 折れた枠(#327 / [ADR-0057](../../../docs/adr/0057-folded-frame.md) の決定 4)
 *
 * **折れた枠は入れ物が 1 つ(セル 0)。** 素材は全体にまたがるので、どの面(セル)に落としても、
 * どのセルの外部キーで `assign` しても**セル 0 に寄せる**。連動先の折れた枠も
 * **セル 0 だけを持つ枠**として数える —— 平面の枠のセル 1 に落としても、折れた枠には入らない。
 *
 * **寄せるのはここだけ。** 素材を入れる入口(S04 のドロップ・ビューアのドロップ・`assign`・`clear`)は
 * すべてこの関数を通る —— 入口ごとに寄せると、破棄の判定(ADR-0039 の決定 4)や空の輪郭とずれる。
 */
export function linkedCells(frames, target) {
    const own = frames.find((frame) => frame.id === target.frameId);
    /*
     * **連動番号の有無より先に寄せる** —— 連動していない折れた枠でも、入れ物はセル 0 の 1 つ。
     * 知らない枠(`own` が無い)は寄せない(下のとおり、そのまま返す)。
     */
    const slot = own !== undefined && isFolded(own) ? { frameId: target.frameId, cellIndex: 0 } : target;
    // **知らない枠はそのまま返す。** 表とテンプレートが一瞬ずれても落とさない。
    if (own?.linkId == null)
        return [slot];
    const cells = frames
        .filter((frame) => frame.linkId === own.linkId && slotCountOf(frame) > slot.cellIndex)
        .map((frame) => ({ frameId: frame.id, cellIndex: slot.cellIndex }));
    /*
     * **自分は必ず含める。**
     *
     * 上の絞り込みは**自分の枠にも同じように効く** —— `cellIndex` が自分のセル数を
     * 超えていると、**落とした先ごと消えて空配列になる。** いまはそこへ至る経路が無い
     * (`cellIndex` はどれも枠の `grid` から出る)が、**空が返ると静かに壊れる** ——
     * 呼び出し側は素材を作り終えており、**どのセルにも載らないまま破棄もされない。**
     *
     * **契約は構造で守る。** 呼び出し側が分岐しないための関数が、分岐を要求してはいけない。
     */
    return cells.some((cell) => cell.frameId === slot.frameId) ? cells : [slot, ...cells];
}
/**
 * 2 つの分割が**同じ内容**か(#177)。
 *
 * ## なぜ [`sameShape`](#sameShape) と別に要るのか
 *
 * `sameShape` は**セルの個数**しか見ない —— 素材の割り当てを保てるかの判定用で、
 * **`16+16:9` と `4+4:3` を同じと言う。**
 *
 * こちらは**値まで見る。** 「未保存か」の判定に使うので、
 * **比率の数字だけを変えた編集を取りこぼしてはいけない**
 * (取りこぼすと**保存ボタンが有効にならない** —— #177 で実際に起きた)。
 *
 * ## 未指定は「未指定同士だけ」等しい
 *
 * `grid` は省略可で、**未指定はクアッドの辺の長さの比に落ちる**
 * (`renderer/src/fit.ts` の `apparentAspect`)。つまり
 * **「未指定」と「`16:9` と明示」は意味が違う** —— 等しくない。
 *
 * `null` と `undefined` は**どちらも「未指定」**なので等しい。
 * ここを分けると、保存の往復で表現が変わっただけで**常に未保存**になる。
 */
export function isEqualGrid(a, b) {
    if (a == null || b == null)
        return a == null && b == null;
    return isEqualTerms(a.columns, b.columns) && isEqualTerms(a.rows, b.rows);
}
/**
 * 項の並びが等しいか。
 *
 * **厳密な数値比較でよい。** 項は [`isGridTerm`](#isGridTerm) が
 * 小数第 6 位までに丸めた有限の正の数だけを通すので、
 * `NaN` も `-0` も入らない。
 */
function isEqualTerms(a, b) {
    if (a.length !== b.length)
        return false;
    return a.every((term, i) => term === b[i]);
}
/**
 * 分割の形が同じか。**セルの意味が変わらないか**を見る。
 *
 * 比率だけが変わった(`16:9` → `1920:1080`)ならセルの並びは同じで、
 * **割り当てはそのまま使える。** 分割の数が変わったら別物 ——
 * 同じ番号が別の場所を指すようになるので、当て直しになる
 * (ADR-0029 が引き受けた負債)。
 */
export function sameShape(a, b) {
    const shapeOf = (grid) => grid == null ? [1, 1] : [grid.columns.length, grid.rows.length];
    const [aColumns, aRows] = shapeOf(a);
    const [bColumns, bRows] = shapeOf(b);
    return aColumns === bColumns && aRows === bRows;
}
/**
 * セルを `Map` の鍵にする。
 *
 * 分割した盤面はセルごとに別の素材を持つので、枠 ID だけでは足りない。
 * **描画コア・ビューア・S04 が同じ鍵を使う** —— 別々に組み立てると、
 * 片方が `f1-0`、片方が `f1#0` を作って**割り当てが黙って迷子になる。**
 *
 * 区切りは `#`。枠 ID は API が採番する識別子で `#` を含まない(#28)。
 */
export function cellKey(ref) {
    return `${ref.frameId}#${String(ref.cellIndex)}`;
}
/**
 * すべてのセルを**行優先**(左上から右へ、次の行へ)で返す。
 *
 * `16+16:9+9` なら 4 つ —— `0` 左上 / `1` 右上 / `2` 左下 / `3` 右下。
 */
export function gridCells(grid) {
    const xs = edgesOf(grid.columns);
    const ys = edgesOf(grid.rows);
    const cells = [];
    for (let row = 0; row < grid.rows.length; row += 1) {
        const top = ys[row] ?? 0;
        const bottom = ys[row + 1] ?? 0;
        for (let column = 0; column < grid.columns.length; column += 1) {
            const left = xs[column] ?? 0;
            const right = xs[column + 1] ?? 0;
            cells.push({
                index: row * grid.columns.length + column,
                column,
                row,
                left,
                right,
                top,
                bottom,
                width: right - left,
                height: bottom - top,
            });
        }
    }
    return cells;
}
//# sourceMappingURL=grid.js.map